import { useTranslation } from "_metronic/i18n/language";
import { ApiQuestionType, AudiometryAnswerType } from "app/types";
import { useEffect, useState } from "react";
import { ScoreResultType } from "utils/getScore";
import { calculateTimeDifference } from "utils";
import { ParamsI } from "../CognitiveResults/cognitive-result-view";
import { Box, Button, Card, Title, Header } from "app/pages/admin/components";
import Modal from "app/pages/admin/components/Modal";
import Paragraph from "app/pages/admin/components/Paragraph";
import { Chip } from "@mui/material";
import { Spinner } from "react-bootstrap";
import ModalAudiometryResultPDF from "./ModalAudiometryResultPDF";
import { calculateCorrectAnswers } from "../calculateCorrectAnswers";

type ScoreType = {
  score: number;
  questionId: number;
  categoryCognitiveQuestionId: number;
};

type ModalAudiometryDetailsProps = {
  quizAnswer: AudiometryAnswerType[];
  openModal: boolean;
  setOpenModal: (isOpen: boolean) => void;
  findQuestion: (questionId: number) => Promise<ApiQuestionType>;
  loading: boolean;
  setLoading: (e: boolean) => void;
  accountId: ParamsI | undefined;
  accountSelected: any;
  quizName?: string;
};
const ModalAudiometryDetails = ({
  quizAnswer,
  openModal,
  setOpenModal,
  findQuestion,
  loading,
  accountId,
  accountSelected,
  quizName,
}: ModalAudiometryDetailsProps) => {
  const [totalScore, setTotalScore] = useState<number>(0);
  const [openPDF, setOpenPDF] = useState(false);
  const [dataScore, setDataScore] = useState<ScoreResultType[]>([]);
  const translate = useTranslation();
  const answersWithScores = { ...quizAnswer, dataScore };

  const handleOpenPDF = (isOpen: boolean) => {
    setOpenPDF(isOpen);
  };

  useEffect(() => {
    const getData = async () => {
      setTotalScore(0);

      if (!quizAnswer.length) {
        return;
      }
      const data = getDataScore();
      setDataScore(data);
      const total = data.reduce((acc, curr) => {
        if (curr.categoryCognitiveQuestionId === 2 && curr.questionId === 4) {
          return acc;
        }
        return acc + curr.score;
      }, 0);
      setTotalScore(total);
    };
    getData();
  }, [quizAnswer]);

  const initDate = new Date(quizAnswer[1]?.initDate);
  const endDate = new Date(quizAnswer[quizAnswer.length - 1]?.endDateTime);
  const timeTotal =
    initDate && endDate ? calculateTimeDifference(initDate, endDate) : "N/A";

  const getDataScore = () => {
    const scores: ScoreType[] = quizAnswer.map(answer => {
      const score = calculateCorrectAnswers(answer.answer, answer.rightAnswer);
      return {
        score,
        questionId: answer.questionId,
        categoryCognitiveQuestionId: answer.categoryCognitiveQuestionId,
      };
    });
    return scores;
  };

  return !loading ? (
    <>
      <Modal
        setShowModal={setOpenModal}
        showModal={openModal}
        style={{
          width: "80%",
          padding: "0",
          maxHeight: "600px",
          marginTop: "50px",
        }}
      >
        <Card style={styles.container}>
          <Card.Header>
            <Header>
              {accountSelected.name} - ID: {quizAnswer[0].testResultId}
            </Header>
          </Card.Header>
          <Card.Title>
            <Title style={styles.title}>
              {translate("screenApp.results.answer.title")}
              {quizName ? `: ${quizName}` : ""}
            </Title>
          </Card.Title>
          <Card.Body style={{ padding: "20px" }}>
            <Box style={styles.totalScore}>
              <Paragraph style={{ width: "auto" }}>
                <Paragraph.Bold>
                  {translate("screenApp.results.answer.score.title")}:
                </Paragraph.Bold>{" "}
                {totalScore !== 0 && totalScore}
              </Paragraph>
              {totalScore === 0 && (
                <Spinner style={{ borderWidth: "4px", marginLeft: ".5em" }} />
              )}
            </Box>

            {quizAnswer.some(answer => answer.typeQuiz === "screening") && (
              <>
                <Title style={styles.subtitle}>
                  {translate("screenApp.results.answer.screening")}:
                </Title>
                {quizAnswer
                  .filter(answer => answer.typeQuiz === "screening")
                  .map(answer => (
                    <DisplayAnswer
                      key={answer.id}
                      answer={answer}
                      findQuestion={findQuestion}
                    />
                  ))}
              </>
            )}

            {quizAnswer.some(answer => answer.typeQuiz === "anamnese") && (
              <>
                <Title style={styles.subtitle}>
                  {translate("screenApp.results.answer.initials")}:
                </Title>
                {quizAnswer
                  .filter(answer => answer.typeQuiz === "anamnese")
                  .map(answer => (
                    <DisplayAnswer
                      key={answer.id}
                      answer={answer}
                      findQuestion={findQuestion}
                    />
                  ))}
              </>
            )}

            {quizAnswer.some(answer => answer.typeQuiz === "quiz") && (
              <>
                <Title style={styles.subtitle}>
                  {translate("screenApp.results.answer.main")}:
                </Title>
                {quizAnswer
                  .filter(answer => answer.typeQuiz === "quiz")
                  .map(answer => (
                    <DisplayAnswer
                      key={answer.id}
                      answer={answer}
                      findQuestion={findQuestion}
                      pressable
                      time={true}
                    />
                  ))}
              </>
            )}

            <Paragraph
              style={{
                fontSize: "20px",
                marginBottom: "16px",
                textAlign: "left",
                fontWeight: "bold",
                borderColor: "#ccc",
                padding: "4px 10px",
                borderRadius: "5px",
              }}
            >
              {translate("screenApp.results.answer.total.time")}: {timeTotal}
            </Paragraph>
          </Card.Body>

          <Card.Actions
            style={{
              justifyContent: "center",
              flexDirection: "row",
              display: "flex",
            }}
          >
            <Button
              style={styles.buttom}
              onClick={() => {
                handleOpenPDF(true);
              }}
            >
              {translate("screenApp.results.openPdf").toUpperCase()}
            </Button>

            <ModalAudiometryResultPDF
              openPDF={openPDF}
              setOpenPDF={handleOpenPDF}
              data={answersWithScores}
              accountId={accountId}
              account={accountSelected}
              totalScore={totalScore}
              quizAnswer={quizAnswer}
            />
          </Card.Actions>
        </Card>
      </Modal>
    </>
  ) : (
    <></>
  );
};

type DisplayAnswerProps = {
  answer: any;
  pressable?: boolean;
  time?: boolean;
  findQuestion: (questionId: number) => Promise<ApiQuestionType>;
};
const DisplayAnswer = ({
  answer,
  pressable = false,
  time = false,
  findQuestion,
}: DisplayAnswerProps) => {
  const [question, setQuestion] = useState({} as ApiQuestionType);
  const translate = useTranslation();

  useEffect(() => {
    const start = async () => {
      const data = await findQuestion(answer.questionId);
      setQuestion(data);
    };
    start();
  }, []);

  const timeDiff = calculateTimeDifference(
    new Date(answer.initDate),
    new Date(answer.endDateTime),
  );

  let dataScore: number;
  if (question) {
    dataScore = calculateCorrectAnswers(answer.answer, answer.rightAnswer);
  }

  const renderStyledChip = (label: string) => (
    <Chip
      label={label}
      variant="outlined"
      sx={{
        boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
        fontSize: "1.2rem",
        height: "38px",
        minWidth: "150px",
      }}
    />
  );

  const renderChipScore = () => {
    const score = dataScore ?? 0;

    if (answer.categoryCognitiveQuestionId === 2 && answer.questionId === 4) {
      return renderStyledChip(
        translate("screenApp.results.answer.does.not.score"),
      );
    }

    if (score !== null && score >= 0) {
      return renderStyledChip(`${translate("labels.score")}: ${score}`);
    }

    return renderStyledChip("Erro");
  };

  return (
    answer.endDateTime && (
      <Card key={answer.id} style={styles.answerContainer}>
        <Card.Title>
          {" "}
          {`${translate("screenApp.results.question")}: ${answer.question}`}
        </Card.Title>
        <Card.Body>
          {answer.rightAnswer && (
            <Paragraph style={styles.answer}>
              <Paragraph.Bold>
                {translate("screenApp_results_answer_true")}:
              </Paragraph.Bold>{" "}
              {answer.rightAnswer}
            </Paragraph>
          )}
          <Paragraph style={styles.answer}>
            <Paragraph.Bold>
              {translate("screens_quiz_labels_answer")}:
            </Paragraph.Bold>{" "}
            {answer.answer}
          </Paragraph>
          <Paragraph style={styles.date}>
            <Paragraph.Bold>{translate("labels_home")}:</Paragraph.Bold>{" "}
            {new Date(answer.initDate).toLocaleString()}
          </Paragraph>
          <Paragraph style={styles.date}>
            <Paragraph.Bold> {translate("labels_end")}:</Paragraph.Bold>{" "}
            {new Date(answer.endDateTime).toLocaleString()}
          </Paragraph>
        </Card.Body>

        {pressable && (
          <Card.Actions
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "10px",
            }}
          >
            <Box
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "start",
                gap: "10px",
                padding: "0",
              }}
            >
              {renderChipScore()}
              {time && <Paragraph>{timeDiff}</Paragraph>}
            </Box>
          </Card.Actions>
        )}
      </Card>
    )
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: "flex",
    flexDirection: "column",
    padding: "0",
    margin: "0",
  },
  title: {
    fontSize: "34px",
    marginBottom: "16px",
    textAlign: "center",
  },
  totalScore: {
    fontSize: "20px",
    marginBottom: "16px",
    fontWeight: "bold",
    borderWidth: "1px",
    borderStyle: "solid",
    borderColor: "#ccc",
    padding: "4px 10px",
    borderRadius: "5px",

    justifyContent: "center",
  },
  subtitle: {
    fontWeight: "bold",
    textAlign: "start",
    fontSize: "20px",
    margin: "16px 0",
  },
  timetotal: {
    fontSize: "20px",
    margin: "16px 0",
    textAlign: "right",
  },
  answerContainer: {
    marginBottom: "20px",
    borderRadius: "8px",
    paddingBottom: "8px",
    boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
  },
  answerTextContainer: {
    flex: 1,
  },
  question: {
    fontSize: "18px",
    fontWeight: "bold",
  },
  answer: {
    fontSize: "16px",
    margin: "5px 0",
    fontWeight: "bold",
  },
  date: {
    fontSize: "14px",
    color: "gray",
  },
  timeDifference: {
    fontSize: "14px",
    fontWeight: "bold",
    textAlign: "left",
    alignSelf: "center",
  },
  button: {
    backgroundColor: "#007bff",
    color: "#fff",
    padding: "10px 20px",
    borderRadius: "8px",
    fontSize: "16px",
    fontWeight: "bold",
    border: "none",
    cursor: "pointer",
    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.2)",
    transition: "all 0.3s ease",
    outline: "none",
  },
  bottomView: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
  },
};

export default ModalAudiometryDetails;
